import React from "react";
import { Table, Form, Input, Typography, Col, Row, Tooltip, Divider } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import ToothChart from "@/components/tooth-chart/ToothChart";

const { Text } = Typography;

const Cell = ({ label, name, control, suffix, description, required }) => (
    <div className="odt-cell">
        <div className="odt-label">
            {label} {suffix ? <sup>({suffix})</sup> : null}
            {description && (
                <Tooltip title={description}>
                    <span
                        style={{
                            marginLeft: 6,
                            color: "#1890ff",
                            cursor: "pointer",
                            fontSize: "14px",
                        }}
                    >
                        <QuestionCircleOutlined />
                    </span>
                </Tooltip>
            )}
        </div>

        <Form.Item
            name={name}
            style={{ margin: 0 }}
            rules={[{ required: required, message: "Bắt buộc nhập" }]}
        >
            {control}
        </Form.Item>
    </div>
);


export default function DiagnosticSection(props) {

    const { onToothDataChange, toothData } = props;

    const columns = [
        {
            title: "",
            dataIndex: "group",
            key: "group",
            width: 100,
            className: "odt-group-title",
            render: (text) => (
                <div className="odt-group" dangerouslySetInnerHTML={{ __html: text }} />
            ),
        },
        {
            title: <>Chiều trước - sau<sup>(A)</sup></>,
            dataIndex: "a",
            width: 300,
            key: "a",
            render: (val, record) => {
                if (record.isFullWidth) {
                    return { children: val, props: { colSpan: 3 } }; // gộp A+B+C
                }
                return val;
            },
        },
        {
            title: <>Chiều dọc<sup>(B)</sup></>,
            dataIndex: "b",
            width: 300,
            key: "b",
            render: (val, record) => {
                if (record.isFullWidth) return { props: { colSpan: 0 } }; // ẩn cột B
                return val;
            },
        },
        {
            title: <>Chiều ngang<sup>(C)</sup></>,
            dataIndex: "c",
            width: 300,
            key: "c",
            render: (val, record) => {
                if (record.isFullWidth) return { props: { colSpan: 0 } }; // ẩn cột C
                return val;
            },
        },
    ];

    const dataSource = [
        // ==== XƯƠNG (I) ====
        {
            key: "bone-1",
            group: "Xương<sup>(I)</sup>",
            a: (
                <>
                    <Cell
                        label="Hạng mặt"
                        suffix="1"
                        name={["a", "hang_mat"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(1/2/3)"
                            />
                        }
                    />
                    <Cell
                        label="Kích thước xương Hàm trên"
                        suffix="2"
                        name={["a", "kich_thuoc_xuong_ham_tren"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(nhỏ/bình thường/lùi)"
                            />
                        }
                    />
                    <Cell
                        label="Kích cỡ xương hàm dưới"
                        suffix="3"
                        name={["a", "kich_thuoc_xuong_ham_duoi"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(nhỏ/bình thường/lùi)"
                            />
                        }
                    />
                </>
            ),
            b: (
                <>
                    <Cell
                        label="Xu hướng phát triển"
                        suffix="1"
                        name={["b", "xu_huong_phat_trien"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(góc đóng/góc mở/trung bình)"
                            />
                        }
                    />
                    <Cell
                        label="Cant mặt phẳng hàm trên"
                        suffix="2"
                        name={["b", "cant_mat_phang_ham_tren"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(có/không)"
                            />
                        }
                    />
                </>
            ),
            c: (
                <>
                    <Cell
                        label="Xương hàm trên"
                        suffix="1"
                        name={["c", "xuong_ham_tren"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(hẹp/rộng/bình thường)"
                            />
                        }
                    />
                    <Cell
                        label="Xương hàm dưới"
                        suffix="2"
                        name={["c", "xuong_ham_duoi"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(hẹp/rộng/bình thường)"
                            />
                        }
                    />
                </>
            ),
        },
        // ==== RĂNG (II) ====
        {
            key: "tooth-1",
            group: "Răng<sup>(II)</sup>",
            a: (
                <>
                    <Row gutter={8}>
                        <Col span={14}>
                            <Cell
                                label="Tương quan R6 (Phải)"
                                suffix="1"
                                name={["a", "tuong_quan_r6_phai"]}
                                required={false}
                                control={
                                    <Input
                                        allowClear
                                        className="w-100"
                                        placeholder="(hạng 1/ hạng 2/ hạng 3)"
                                    />
                                }
                            />
                        </Col>
                        <Col span={10}>
                            <Cell
                                label="&nbsp;"
                                name={["a", "tuong_quan_r6_phai_mm"]}
                                required={false}
                                control={
                                    <Input
                                        allowClear
                                        className="w-100"
                                        suffix="mm"
                                    />
                                }
                            />
                        </Col>
                    </Row>
                    <Row gutter={8}>
                        <Col span={14}>
                            <Cell
                                label="Tương quan R6 (Trái)"
                                suffix="2"
                                name={["a", "tuong_quan_r6_trai"]}
                                required={false}
                                control={
                                    <Input
                                        allowClear
                                        className="w-100"
                                        placeholder="(hạng 1/ hạng 2/ hạng 3)"
                                    />
                                }
                            />
                        </Col>
                        <Col span={10}>
                            <Cell
                                label="&nbsp;"
                                name={["a", "tuong_quan_r6_trai_mm"]}
                                required={false}
                                control={
                                    <Input
                                        allowClear
                                        className="w-100"
                                        suffix="mm"
                                    />
                                }
                            />
                        </Col>
                    </Row>
                    <Row gutter={8}>
                        <Col span={14}>
                            <Cell
                                label="Tương quan R3 (Phải)"
                                suffix="3"
                                name={["a", "tuong_quan_r3_phai"]}
                                required={false}
                                control={
                                    <Input
                                        allowClear
                                        className="w-100"
                                        placeholder="(hạng 1/ hạng 2/ hạng 3)"
                                    />
                                }
                            />
                        </Col>
                        <Col span={10}>
                            <Cell
                                label="&nbsp;"
                                name={["a", "tuong_quan_r3_phai_mm"]}
                                required={false}
                                control={
                                    <Input
                                        allowClear
                                        className="w-100"
                                        suffix="mm"
                                    />
                                }
                            />
                        </Col>
                    </Row>
                    <Row gutter={8}>
                        <Col span={14}>
                            <Cell
                                label="Tương quan R3 (Trái)"
                                suffix="4"
                                name={["a", "tuong_quan_r3_trai"]}
                                required={false}
                                control={
                                    <Input
                                        allowClear
                                        className="w-100"
                                        placeholder="(hạng 1/ hạng 2/ hạng 3)"
                                    />
                                }
                            />
                        </Col>
                        <Col span={10}>
                            <Cell
                                label="&nbsp;"
                                name={["a", "tuong_quan_r3_trai_mm"]}
                                required={false}
                                control={
                                    <Input
                                        allowClear
                                        className="w-100"
                                        suffix="mm"
                                    />
                                }
                            />
                        </Col>
                    </Row>
                    <Cell
                        label="Cắn chìa"
                        suffix="5"
                        name={["a", "can_chia"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                suffix="mm"
                            />
                        }
                    />
                    <Cell
                        label="Cắn ngược răng cửa / nanh"
                        suffix="6"
                        name={["a", "can_nguc_rang_cua_nanh"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(có/không)"
                            />
                        }
                    />
                    <Cell
                        label="Tại rặng"
                        name={["a", "can_nguc_rang_cua_nanh_tai_rang"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="Ghi rõ nếu có"
                            />
                        }
                    />
                </>
            ),
            b: (
                <>
                    <Cell
                        label="Tương quan răng cửa"
                        suffix="1"
                        name={["b", "tuong_quan_rang_cua"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(bình thường/ cắn hở/ cắn sâu)"
                            />
                        }
                    />
                    <Cell
                        label="Cắn trùm / hở"
                        suffix="2"
                        name={["b", "can_trum_ho"]}
                        required={false}
                        description="Giá trị dương cho cắn trùm, giá trị âm cho cắn hở"
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                suffix="mm"
                            />
                        }
                    />
                    <Cell
                        label="Đường cười"
                        suffix="3"
                        name={["b", "duong_cuoi"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(cao/ thấp/ trung bình)"
                            />
                        }
                    />
                    <Cell
                        label="Cười hở lợi"
                        suffix="4"
                        name={["b", "cuoi_ho_loi"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                suffix="mm"
                            />
                        }
                    />
                    <Row gutter={8} style={{ marginBottom: '-10px' }}>
                        <Col span={12}>
                            <Cell
                                label="Từ răng"
                                suffix="5"
                                name={["b", "cuoi_ho_loi_tu_rang"]}
                                required={false}
                                control={
                                    <Input
                                        allowClear
                                        className="w-100"
                                    />
                                }
                            />
                        </Col>
                        <Col span={12}>
                            <Cell
                                label="Đến răng"
                                name={["b", "cuoi_ho_loi_den_rang"]}
                                required={false}
                                control={
                                    <Input
                                        allowClear
                                        className="w-100"
                                    />
                                }
                            />
                        </Col>
                    </Row>
                    <Cell
                        label="Cung cười"
                        name={["b", "cung_cuoi"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(hài hoà / dẹt / ngược)"
                            />
                        }
                    />
                    <Cell
                        label="Mặt phẳng nhai"
                        suffix="6"
                        name={["b", "mat_phang_nhai"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(bình thường/ hướng đóng/ hướng mở)"
                            />
                        }
                    />
                </>
            ),
            c: (
                <>
                    <Cell
                        label="Đường giữa hàm trên so với đường giữa mặt"
                        suffix="1"
                        name={["c", "duong_giua_ham_tren_mat"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(trùng/ lệch và song song/ nghiêng)"
                            />
                        }
                    />
                    <Cell
                        label="Mức độ lệch"
                        suffix="2"
                        name={["c", "duong_giua_ham_tren_mat_muc_do_lech"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                suffix="mm"
                            />
                        }
                    />
                    <Cell
                        label="Đường giữa hàm dưới so với đường giữa mặt"
                        suffix="3"
                        name={["c", "duong_giua_ham_duoi_mat"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(trùng/  lệch và song song/ nghiêng)"
                            />
                        }
                    />
                    <Cell
                        label="Mức độ lệch"
                        suffix="4"
                        name={["c", "duong_giua_ham_duoi_mat_muc_do_lech"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                suffix="mm"
                            />
                        }
                    />
                    <Cell
                        label="Cắn chéo răng hàm"
                        suffix="5"
                        name={["c", "can_cheo_rang_ham"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(có/ không)"
                            />
                        }
                    />
                    <Cell
                        label="Tại răng"
                        name={["c", "can_cheo_rang_ham_tai_rang"]}
                        required={false}
                        control={
                            <Input.TextArea
                                allowClear
                                className="w-100"
                                placeholder="ghi rõ nếu có"
                            />
                        }
                    />
                    <Cell
                        label="Cắn cắt kéo răng hàm"
                        suffix="6"
                        name={["c", "can_cat_keo_rang_ham"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(có/ không)"
                            />
                        }
                    />
                    <Cell
                        label="Tại răng"
                        name={["c", "can_cat_keo_rang_ham_tai_rang"]}
                        required={false}
                        control={
                            <Input.TextArea
                                allowClear
                                className="w-100"
                                placeholder="ghi rõ nếu có"
                            />
                        }
                    />
                    <Cell
                        label="Thừa khoảng / thiếu khoảng hàm trên"
                        suffix="7"
                        name={["c", "thua_khoang_thieu_khoang_ham_tren"]}
                        required={false}
                        description="Giá trị dương cho thiếu khoảng - chen chúc, giá trị âm cho thừa khoảng - khe thưa"
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                suffix="mm"
                            />
                        }
                    />
                    <Cell
                        label="Thừa khoảng / thiếu khoảng dưới"
                        suffix="8"
                        name={["c", "thua_khoang_thieu_khoang_ham_duoi"]}
                        required={false}
                        description="Giá trị dương cho thiếu khoảng - chen chúc, giá trị âm cho thừa khoảng - khe thưa"
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                suffix="mm"
                            />
                        }
                    />
                </>
            ),
        },

        // ==== CHỨC NĂNG (III) ====
        {
            key: "func-1",
            group: "Chức năng<sup>(III)</sup>",
            isFullWidth: true,
            a: (
                <>
                    <Text>Khớp thái dương hàm</Text>
                    <Cell
                        label="Tiếng kêu bất thường"
                        suffix="1"
                        name={["a", "tieng_keu_bat_thuong"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(có/ không có?)"
                            />
                        }
                    />
                    <Cell
                        label="Há/ ngậm tối đa"
                        suffix="2"
                        name={["b", "ha_ngam_toi_da"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(thẳng/ zig zag sang trái/ zig zag sang phải)"
                            />
                        }
                    />
                    <Cell
                        label="X-ray tiêu lồi"
                        suffix="3"
                        name={["b", "x_ray_tieu_loi"]}
                        required={false}
                        control={
                            <Input
                                allowClear
                                className="w-100"
                                placeholder="(tiêu lồi cầu bên trái/ bên phải/ hai bên?)"
                            />
                        }
                    />
                </>
            )
        },
    ];

    return (
        <>
            <style>{`
                .ortho-table .ant-table-cell {
                    vertical-align: baseline !important;
                }
                .ortho-table .odt-group-title {
                    vertical-align: middle !important;
                    font-weight: bold !important;
                }
                .ortho-table .odt-cell {
                    margin: 15px 0 5px 0;
                    
                }
                .ortho-table .ant-form-item-extra {
                    font-style: italic;
                }
                .ortho-table .odt-label {
                    letter-spacing: -0.5px;
                }
            `}</style>
            <Table
                bordered={false}
                pagination={false}
                columns={columns}
                size="small"
                dataSource={dataSource}
                className="ortho-table"
                rowClassName={(_, index) =>
                    index % 2 === 0 ? "ortho-row-even" : "ortho-row-odd"
                }
            />

            <Divider orientation="left" orientationMargin="0">Kích thước răng và hình thể (mm)<sup>(IV.1)</sup></Divider>
            <ToothChart data={toothData} onDataChange={onToothDataChange} />
            <div className="ortho-table">
                <Row gutter={8} className="mt-3">
                    <Col span={12}>
                        <Cell
                            label="Chỉ số Bolton B6"
                            name={["b", "chi_so_bolton_b6"]}
                            required={false}
                            control={
                                <Input
                                    allowClear
                                    className="w-100"
                                />
                            }
                        />
                    </Col>
                    <Col span={12}>
                        <Cell
                            label="Chỉ số Bolton B12"
                            name={["b", "chi_so_bolton_b12"]}
                            required={false}
                            control={
                                <Input
                                    allowClear
                                    className="w-100"
                                />
                            }
                        />
                    </Col>
                    <Col span={24}>
                        <Divider orientation="left" orientationMargin="0">Các ghi chú khác (răng, xương, mô mềm)<sup>(IV.2)</sup></Divider>
                        <Form.Item
                            name="cac_ghi_chu_khac"
                            rules={[{ required: false, message: "Bắt buộc nhập" }]}
                        >
                            <Input.TextArea
                                allowClear
                                className="w-100"
                                placeholder="Ví dụ: răng 12 hình thể nhỏ, dị dạng, chân răng ngắn"
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </div>
        </>
    );
}
